import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardActionArea,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    TextField,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ReactComponent as SiemensIcon } from "../../assets/icons/siemens_icon.svg";
import { ReactComponent as PhilipsIcon } from "../../assets/icons/philips_icon.svg";
import { ReactComponent as GEIcon } from "../../assets/icons/GE_healthcare_icon.svg";
import { ReactComponent as GEDarkIcon } from "../../assets/icons/GE_healthcare_dark_icon.svg";
import { ReactComponent as FujifilmIcon } from "../../assets/icons/fujifilm_icon.svg";
import { ReactComponent as FujifilmDarkIcon } from "../../assets/icons/fujifilm_dark_icon.svg";
import { ReactComponent as MRIIcon } from "../../assets/icons/mri_icon.svg";
import { ReactComponent as MRIDarkIcon } from "../../assets/icons/mri_dark_icon.svg";
import { ReactComponent as HighSpecIcon } from "../../assets/icons/high_spec_icon.svg";
import { ReactComponent as HighSpecDarkIcon } from "../../assets/icons/high_spec_dark_icon.svg";
import { ReactComponent as UnitedImagingIcon } from "../../assets/icons/united_imaging_icon.svg";
import { ReactComponent as UnitedImagingDarkIcon } from "../../assets/icons/united_imaging_dark_icon.svg";
import { ReactComponent as HitachiIcon } from "../../assets/icons/hitachi_logo.svg";
import { ReactComponent as HitachiDarkIcon } from "../../assets/icons/hitachi_dark_logo.svg";

const magnets = [
    {
        id: "siemens",
        name: "Siemens",
        icon: SiemensIcon,
        hasFieldStrength: false,
    },
    {
        id: "ge",
        name: "GE Healthcare",
        icon: GEIcon,
        darkIcon: GEDarkIcon,
        hasFieldStrength: true,
    },
    {
        id: "philips",
        name: "Philips",
        icon: PhilipsIcon,
        hasFieldStrength: false,
    },
    {
        id: "fujifilm",
        name: "Fujifilm",
        icon: FujifilmIcon,
        darkIcon: FujifilmDarkIcon,
        hasFieldStrength: false,
    },
    {
        id: "hitachi",
        name: "Hitachi",
        icon: HitachiIcon,
        darkIcon: HitachiDarkIcon,
        hasFieldStrength: false,
    },
    {
        id: "united_imaging",
        name: "United Imaging",
        icon: UnitedImagingIcon,
        darkIcon: UnitedImagingDarkIcon,
        hasFieldStrength: false,
    },
    {
        id: "other",
        name: "Other",
        icon: MRIIcon,
        darkIcon: MRIDarkIcon,
        hasFieldStrength: false,
    },
    {
        id: "high_spec",
        name: "High Spec",
        icon: HighSpecIcon,
        darkIcon: HighSpecDarkIcon,
        hasFieldStrength: false,
    },
];

export default function MagnetCarousel({ value, onChange, darkState }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const [selectedMagnet, setSelectedMagnet] = useState(null);
    const [fieldStrength, setFieldStrength] = useState("1.5");
    const [customOtherName, setCustomOtherName] = useState("Other");
    const [startIndex, setStartIndex] = useState(0);

    const itemsToShow = isMobile ? 1 : isTablet ? 2 : 3;

    // Debug dark mode
    useEffect(() => {
        console.log("MagnetCarousel darkState:", darkState, typeof darkState);
    }, [darkState]);

    // Debug selected magnet
    useEffect(() => {
        console.log("selectedMagnet changed to:", selectedMagnet);
    }, [selectedMagnet]);

    // Initialize from value prop
    useEffect(() => {
        if (value) {
            let magnetId;
            if (value.startsWith("GE")) {
                magnetId = "ge";
                setSelectedMagnet("ge");
                setFieldStrength(value.includes("1.5") ? "1.5" : "3.0");
            } else {
                const magnet = magnets.find(
                    (m) => m.name.toLowerCase() === value.toLowerCase(),
                );
                if (magnet) {
                    magnetId = magnet.id;
                    setSelectedMagnet(magnet.id);
                } else if (value && value !== "Other") {
                    // If value doesn't match any predefined magnet, treat it as custom "other"
                    magnetId = "other";
                    setSelectedMagnet("other");
                    setCustomOtherName(value);
                } else if (value === "Other") {
                    magnetId = "other";
                    setSelectedMagnet("other");
                    setCustomOtherName("Other");
                }
            }

            // Update startIndex to show the selected magnet
            if (magnetId) {
                const magnetIndex = magnets.findIndex((m) => m.id === magnetId);
                if (magnetIndex !== -1) {
                    // Calculate startIndex to show the selected magnet
                    const newStartIndex = Math.max(
                        0,
                        Math.min(magnetIndex, magnets.length - itemsToShow),
                    );
                    setStartIndex(newStartIndex);
                }
            }
        }
    }, [value, itemsToShow]);

    // Build the full value string
    const buildValue = (magnetId, strength, otherName) => {
        if (magnetId === "ge") {
            return `GE ${strength}T`;
        }
        if (magnetId === "other") {
            return otherName || "Other";
        }
        const magnet = magnets.find((m) => m.id === magnetId);
        return magnet ? magnet.name : "";
    };

    const handleMagnetSelect = (magnetId) => {
        setSelectedMagnet(magnetId);
        const magnet = magnets.find((m) => m.id === magnetId);

        if (magnet.hasFieldStrength) {
            // For GE, use current field strength
            onChange(buildValue(magnetId, fieldStrength, customOtherName));
        } else if (magnetId === "other") {
            // For other, use custom name
            onChange(buildValue(magnetId, fieldStrength, customOtherName));
        } else {
            onChange(magnet.name);
        }
    };

    const handleFieldStrengthChange = (event, newStrength) => {
        if (newStrength !== null) {
            setFieldStrength(newStrength);
            onChange(buildValue(selectedMagnet, newStrength, customOtherName));
        }
    };

    const handleCustomOtherNameChange = (event) => {
        const newName = event.target.value;
        setCustomOtherName(newName);
        onChange(newName || "Other");
    };

    const handlePrevious = () => {
        setStartIndex(Math.max(0, startIndex - 1));
    };

    const handleNext = () => {
        setStartIndex(Math.min(magnets.length - itemsToShow, startIndex + 1));
    };

    const visibleMagnets = magnets.slice(startIndex, startIndex + itemsToShow);
    const canGoPrevious = startIndex > 0;
    const canGoNext = startIndex < magnets.length - itemsToShow;

    return (
        <Box sx={{ width: "100%" }}>
            <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Select Magnet Manufacturer
            </Typography>

            {/* Carousel Container */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    mb: 3,
                }}
            >
                {/* Previous Button */}
                <IconButton
                    onClick={handlePrevious}
                    disabled={!canGoPrevious}
                    sx={{
                        visibility:
                            magnets.length > itemsToShow ? "visible" : "hidden",
                    }}
                >
                    <ChevronLeft />
                </IconButton>

                {/* Magnet Cards */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flex: 1,
                        justifyContent: "center",
                        maxWidth: isMobile ? 300 : isTablet ? 550 : 800,
                    }}
                >
                    {visibleMagnets.map((magnet) => {
                        // Select icon based on dark mode
                        let IconComponent;
                        if (darkState && magnet.darkIcon) {
                            IconComponent = magnet.darkIcon;
                            console.log(`${magnet.name}: USING DARK ICON`);
                        } else {
                            IconComponent = magnet.icon;
                            console.log(
                                `${
                                    magnet.name
                                }: USING LIGHT ICON (darkState=${darkState}, hasDarkIcon=${!!magnet.darkIcon})`,
                            );
                        }
                        const isSelected = selectedMagnet === magnet.id;

                        return (
                            <Card
                                key={magnet.id}
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    border: isSelected ? 2 : 0,
                                    borderColor: "primary.main",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                <CardActionArea
                                    onClick={() =>
                                        handleMagnetSelect(magnet.id)
                                    }
                                    sx={{
                                        p: 3,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minHeight: 180,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "80px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 2,
                                            "& svg": {
                                                maxWidth: "100% !important",
                                                maxHeight: "80px !important",
                                                height: "auto !important",
                                                width: "auto !important",
                                            },
                                        }}
                                    >
                                        <IconComponent />
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        fontWeight={isSelected ? 600 : 400}
                                        color={
                                            isSelected
                                                ? "primary"
                                                : "textSecondary"
                                        }
                                        align="center"
                                    >
                                        {magnet.name}
                                    </Typography>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Box>

                {/* Next Button */}
                <IconButton
                    onClick={handleNext}
                    disabled={!canGoNext}
                    sx={{
                        visibility:
                            magnets.length > itemsToShow ? "visible" : "hidden",
                    }}
                >
                    <ChevronRight />
                </IconButton>
            </Box>

            {/* Field Strength Selector for GE */}
            {(() => {
                console.log(
                    "Rendering field strength? selectedMagnet =",
                    selectedMagnet,
                    "condition result:",
                    selectedMagnet === "ge",
                );
                return selectedMagnet === "ge";
            })() && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 3,
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                        Select Field Strength
                    </Typography>
                    <ToggleButtonGroup
                        value={fieldStrength}
                        exclusive
                        onChange={handleFieldStrengthChange}
                        aria-label="field strength"
                        size={isMobile ? "medium" : "large"}
                    >
                        <ToggleButton
                            value="1.5"
                            aria-label="1.5 tesla"
                            sx={{
                                px: isMobile ? 3 : 4,
                                py: isMobile ? 1 : 1.5,
                                minWidth: isMobile ? 100 : 120,
                            }}
                        >
                            <Typography variant={isMobile ? "body1" : "h6"}>
                                1.5T
                            </Typography>
                        </ToggleButton>
                        <ToggleButton
                            value="3.0"
                            aria-label="3.0 tesla"
                            sx={{
                                px: isMobile ? 3 : 4,
                                py: isMobile ? 1 : 1.5,
                                minWidth: isMobile ? 100 : 120,
                            }}
                        >
                            <Typography variant={isMobile ? "body1" : "h6"}>
                                3.0T
                            </Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            {/* Custom Name Input for Other */}
            {selectedMagnet === "other" && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 3,
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                        Enter Custom Magnet Name
                    </Typography>
                    <TextField
                        value={customOtherName}
                        onChange={handleCustomOtherNameChange}
                        placeholder="Other"
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{
                            width: isMobile ? "100%" : "300px",
                        }}
                        inputProps={{
                            "aria-label": "custom magnet name",
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
